import { FC, ReactElement, useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { Navigate, useLocation } from 'react-router-dom';
import React from 'react';
import { useClerk } from '@clerk/clerk-react';
import { useAppSelector } from '@/hooks/useAppSelector';
import BatJetLogo from '@/assets/images/logo512.png';
interface PublicRouteProps {
  children: ReactElement;
  redirectTo?: string;
}

const PublicRoute: FC<PublicRouteProps> = ({ children, redirectTo = '/dashboard' }) => {
  const { user, loading } = useAuth();
  const isLoggingIn = useAppSelector((state) => state.auth.isLoggingIn);
  const location = useLocation();
  const { signOut } = useClerk();
  const [isCheckingSession, setIsCheckingSession] = useState(true);
  const [hasValidSession, setHasValidSession] = useState(false);

  useEffect(() => {
    const checkSessionPersistence = async () => {
      // Skip check if actively logging in
      if (loading || isLoggingIn) {
        setIsCheckingSession(false);
        return;
      }

      if (user) {
        // Check if session should persist based on remember me preference
        const hasRememberMe = localStorage.getItem('clerkAuthSession') === 'true';
        const hasSessionAuth = sessionStorage.getItem('clerkAuthSession') === 'true';

        if (hasRememberMe || hasSessionAuth) {
          // Valid session exists
          setHasValidSession(true);
        } else {
          // User is signed in but no valid auth flag exists
          // This means they logged in without "Remember Me" and opened a new tab
          console.log('Session persistence check failed in PublicRoute, signing out...');
          try {
            // Clear any remaining flags
            localStorage.removeItem('clerkAuthSession');
            localStorage.removeItem('rememberMe');
            sessionStorage.removeItem('clerkAuthSession');

            await signOut();
          } catch (error) {
            console.error('Error signing out:', error);
          }
          setHasValidSession(false);
        }
      } else {
        // No user, reset valid session flag
        setHasValidSession(false);
      }

      setIsCheckingSession(false);
    };

    checkSessionPersistence();
  }, [user, loading, isLoggingIn, signOut]);

  // Show loading only while checking session persistence on initial load
  if (loading || (isCheckingSession && !isLoggingIn)) {
    return (
      <div className="flex flex-col gap-2 items-center justify-center h-screen">
        <img className="max-w-[100px]" src={BatJetLogo} alt="BatJet Logo" />
      </div>
    );
  }

  // Only redirect to dashboard if user has a valid session AND not currently logging in
  if (user && !isLoggingIn && hasValidSession) {
    return <Navigate to={redirectTo} state={{ from: location.pathname }} replace />;
  }

  return children;
};

export default PublicRoute;
